import React from 'react';
import { useQuery, useMutation } from 'react-query';
import api from '../../services/api';
import toast from 'react-hot-toast';
import { Server, Database, RefreshCw, Activity, Terminal, AlertTriangle, ShieldCheck, CheckCircle2, XCircle } from 'lucide-react';

export default function SystemPage() {
  const { data: status, refetch } = useQuery('system-status', () => api.get('/system/status').then(r => r.data), { refetchInterval: 30000 });
  const { data: info } = useQuery('system-info', () => api.get('/system/info').then(r => r.data), { refetchInterval: 60000 });
  const { data: logs } = useQuery('system-logs', () => api.get('/system/logs?lines=50').then(r => r.data), { refetchInterval: 15000 });
  // Verificador de red (STUN/TURN/TURNS) — manual, no auto-poll: cada corrida
  // abre conexiones TCP/UDP/TLS reales, no vale la pena hacerlo cada 15-30s.
  const { data: netCheck, isFetching: netChecking, refetch: runNetCheck } =
    useQuery('network-check', () => api.get('/system/network-check').then(r => r.data),
      { enabled: false, retry: false });

  const reloadMut = useMutation(
    (module) => api.post('/system/asterisk/reload', { module }),
    { onSuccess: () => { toast.success('Recarga exitosa'); refetch(); },
      onError: (e) => toast.error(e.response?.data?.error || 'Error') }
  );

  const fmtUptime = (secs) => {
    const d = Math.floor(secs / 86400); const h = Math.floor((secs % 86400) / 3600);
    const m = Math.floor((secs % 3600) / 60);
    return (d > 0 ? d + 'd ' : '') + h + 'h ' + m + 'm';
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div><h1 className="text-2xl font-bold text-white">Sistema</h1>
          <p className="text-gray-400 text-sm">Estado del servidor PBX</p></div>
        <button onClick={() => refetch()} className="btn-secondary"><RefreshCw size={16} /> Actualizar</button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-blue-600/20 rounded-xl flex items-center justify-center">
              <Server size={18} className="text-blue-400" />
            </div>
            <div><div className="font-semibold text-white">Asterisk</div>
              <div className="text-xs text-muted">Motor SIP/VoIP</div></div>
            <span className={'ml-auto ' + (status?.asterisk?.connected ? 'badge-success' : 'badge-danger')}>
              {status?.asterisk?.connected ? 'Conectado' : 'Desconectado'}
            </span>
          </div>
          {status?.asterisk?.connected && (
            <div className="text-xs text-gray-400 space-y-1">
              <div>Version: <span className="text-gray-200">{status.asterisk.version}</span></div>
              <div>Llamadas activas: <span className="text-gray-200">{status.asterisk.currentCalls || 0}</span></div>
            </div>
          )}
          <div className="mt-3 flex gap-2">
            <button onClick={() => reloadMut.mutate('')} className="btn-secondary text-xs py-1 flex-1 justify-center">
              <RefreshCw size={12} /> Recargar
            </button>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-green-600/20 rounded-xl flex items-center justify-center">
              <Database size={18} className="text-green-400" />
            </div>
            <div><div className="font-semibold text-white">PostgreSQL</div>
              <div className="text-xs text-muted">Base de datos</div></div>
            <span className={'ml-auto ' + (status?.database?.connected ? 'badge-success' : 'badge-danger')}>
              {status?.database?.connected ? 'Conectado' : 'Error'}
            </span>
          </div>
          <div className="text-xs text-gray-400">
            <div className="truncate">{status?.database?.version?.split(' ').slice(0,2).join(' ')}</div>
          </div>
        </div>

        <div className="card">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-9 h-9 bg-purple-600/20 rounded-xl flex items-center justify-center">
              <Activity size={18} className="text-purple-400" />
            </div>
            <div><div className="font-semibold text-white">Node.js API</div>
              <div className="text-xs text-muted">Backend</div></div>
            <span className="ml-auto badge-success">Activo</span>
          </div>
          <div className="text-xs text-gray-400 space-y-1">
            <div>Version: <span className="text-gray-200">{status?.server?.nodeVersion}</span></div>
            <div>Uptime: <span className="text-gray-200">{fmtUptime(Math.floor(status?.server?.uptime || 0))}</span></div>
          </div>
        </div>
      </div>

      {info && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {[
            { label: 'Extensiones', value: info.extensions },
            { label: 'Troncales', value: info.trunks },
            { label: 'Colas', value: info.queues },
            { label: 'IVR', value: info.ivr_menus },
            { label: 'Llamadas hoy', value: info.calls_today },
          ].map(i => (
            <div key={i.label} className="card text-center py-3">
              <div className="text-2xl font-bold text-white">{i.value}</div>
              <div className="text-xs text-muted mt-1">{i.label}</div>
            </div>
          ))}
        </div>
      )}

      <div className="card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <ShieldCheck size={16} className="text-gray-400" />
            <h3 className="font-semibold text-white">Verificador de red (audio/llamadas)</h3>
          </div>
          <button onClick={() => runNetCheck()} disabled={netChecking} className="btn-secondary text-xs py-1">
            <RefreshCw size={12} className={netChecking ? 'animate-spin' : ''} /> {netChecking ? 'Probando…' : 'Probar ahora'}
          </button>
        </div>
        <p className="text-xs text-muted mb-3">
          Prueba en vivo STUN/TURN/TURNS (audio WebRTC) contra este mismo servidor. No mide reachability
          desde internet, pero detecta si coturn está caído o sirviendo un certificado incorrecto.
        </p>
        {!netCheck && !netChecking && <div className="text-xs text-muted">Sin ejecutar todavía.</div>}
        {netCheck && (
          <div className="space-y-1.5">
            {netCheck.checks.map((c) => (
              <div key={c.name} className="flex items-start gap-2 text-xs">
                {c.ok
                  ? <CheckCircle2 size={14} className={'mt-0.5 shrink-0 ' + (c.warn ? 'text-yellow-400' : 'text-green-400')} />
                  : <XCircle size={14} className="mt-0.5 shrink-0 text-red-400" />}
                <div>
                  <span className="text-gray-200 font-medium">{c.name}:</span>{' '}
                  <span className={c.ok ? (c.warn ? 'text-yellow-400' : 'text-gray-400') : 'text-red-400'}>{c.detail}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <Terminal size={16} className="text-gray-400" />
          <h3 className="font-semibold text-white">Logs del Sistema</h3>
        </div>
        <div className="bg-gray-950 rounded-xl p-4 font-mono text-xs text-green-400 h-64 overflow-y-auto">
          {(logs?.lines || []).map((line, i) => (
            <div key={i} className={line.includes('error') || line.includes('Error') ? 'text-red-400' :
              line.includes('warn') || line.includes('WARN') ? 'text-yellow-400' : 'text-green-400'}>
              {line}
            </div>
          ))}
          {!logs?.lines?.length && <div className="text-muted">Sin logs disponibles</div>}
        </div>
      </div>
    </div>
  );
}
